import {
  faEnvelope,
  faInfoCircle,
  faPlus
} from '@fortawesome/pro-light-svg-icons';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Paper, Tooltip } from '@mui/material';
import { DataGrid, type GridRenderCellParams } from '@mui/x-data-grid';
import { useAtom } from 'jotai';
import { cloneDeep } from 'lodash';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CheckPermissions } from '@/components/CheckPermissions';
import ContextMenu, { type ContextMenuRefType } from '@/components/ContextMenu';
import LoadingIndicator from '@/components/LoadingIndicator';
import QuickSearch from '@/components/QuickSearch';
import RolesPermissionDialog from '@/components/RolePermissions/RolePermissionsDialog';
import { PERMISSIONS } from '@/constants/auth.constants';
import { PAGE_SIZES } from '@/constants/common';
import { UserAuthKeysDialog } from '@/features/UserAuthKeys';
import UserDeleteDialog from '@/features/UserDeleteDialog';
import UserEditDialog from '@/features/UserEditDialog';
import useConfirm from '@/hooks/useConfirm';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom } from '@/store/user.store';

import { administrationActionsAtom } from '../Administration.store';
import {
  EMPTY_USER,
  RESTRICTED_USERS,
  USER_CONTEXT_MENU_LIST,
  USERS_TABLE_COLUMNS
} from './Users.constants';
import { useUsers } from './Users.hooks';
import { rolesDialogOpenAtom } from './Users.store';
import useStyles from './Users.styles';
import type { User, UserEdit } from './Users.types';

export const Users = () => {
  const { classes } = useStyles();

  const [rolesDialogOpen, setRolesDialogOpen] = useAtom(rolesDialogOpenAtom);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [apiKeysDialogOpen, setApiKeysDialogOpen] = useState(false);
  const [currentUser] = useAtom(userAtom);
  const childRef = useRef<ContextMenuRefType>(null);
  const {
    isFetchingUsers,
    refetchUsers,
    resetMfa,
    search,
    sendInvitationEmail,
    setSearch,
    setUser,
    user,
    users
  } = useUsers();
  const [, setPageActions] = useAtom(administrationActionsAtom);
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const userCanEdit = usePermissionsCheck(PERMISSIONS.USERS_UPDATE);
  const userCanCreate = usePermissionsCheck(PERMISSIONS.USERS_CREATE);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'moreMenu'
  });

  const handleShowContextMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, row: User | UserEdit) => {
      if (childRef.current) {
        const menuList = cloneDeep(USER_CONTEXT_MENU_LIST);

        // Add "Send Invitation Email" option if user hasn't signed in yet and not a service account
        if (
          (row as User).last_sign_in_at === null &&
          selectedOrganizationId !== 0 &&
          !(row as User).is_service_account
        ) {
          menuList.push({
            label: 'Send Invitation Email',
            icon: faEnvelope,
            permission: PERMISSIONS.USERS_UPDATE
          });
        }

        // Filter out "Reset MFA" for service accounts
        const filteredMenuList = (row as User).is_service_account
          ? menuList.filter((item) => item.label !== 'Reset MFA')
          : menuList;

        childRef.current.showContextMenu.apply(this, [
          event.currentTarget,
          filteredMenuList
        ]);
        setUser(cloneDeep(row) as User);
      }
    },
    [setUser, selectedOrganizationId]
  );

  const { ConfirmationModal: ResetMfaModal, confirm: confirmResetMfa } =
    useConfirm({
      title: 'Reset MFA',
      message:
        'Please confirm that you want to reset MFA for the following user.',
      button: 'Confirm'
    });

  const handleResetMfa = async () => {
    const confirmed = await confirmResetMfa();
    if (confirmed) {
      resetMfa.mutate();
    }
    popupState.close();
  };

  const handleContextMenuClick = useCallback(
    (param: string) => {
      if (param === 'Edit') {
        setOpen(true);
      }
      if (param === 'Delete') {
        setDeleteOpen(true);
      }
      if (param === 'Manage API Keys') {
        setApiKeysDialogOpen(true);
      }
      if (param === 'Send Invitation Email') {
        sendInvitationEmail();
      }
      if (param === 'Reset MFA') {
        handleResetMfa();
      }
    },
    [setOpen, setDeleteOpen]
  );

  const handleOpenUserDialog = useCallback((_event: unknown, row: User) => {
    setUser(cloneDeep(row));
    setOpen(true);
  }, []);

  const handleCloseUserDialog = useCallback(
    (reason?: string) => {
      setOpen(false);
      setDeleteOpen(false);
      setApiKeysDialogOpen(false);
      if (reason === 'success') {
        refetchUsers();
      }
    },
    [refetchUsers]
  );

  // Display the New User button if the user has the permission
  useEffect(() => {
    setPageActions(() =>
      userCanCreate ? (
        <Button
          variant="contained"
          onClick={(e) => handleOpenUserDialog(e, EMPTY_USER)}
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          {...(isLayoutMobile && { fullWidth: true })}
        >
          New User
        </Button>
      ) : null
    );
    return () => {
      setPageActions(null);
    };
  }, [userCanCreate]);

  // Hide the context menu button and prevent modifications for system accounts (restricted users)
  const ellipsisMenuBtn = useCallback(
    (params: GridRenderCellParams) => {
      if (RESTRICTED_USERS.includes(params.row.email)) {
        return null;
      }
      return (
        <Button
          variant="twoTone"
          color="secondary"
          size="small"
          className="contextMenuBtn"
          onClick={(e) => handleShowContextMenu(e, params.row)}
        >
          <FontAwesomeIcon icon={faEllipsisV} size="lg" />
        </Button>
      );
    },
    [handleShowContextMenu]
  );

  USERS_TABLE_COLUMNS[USERS_TABLE_COLUMNS.length - 1].renderCell =
    ellipsisMenuBtn;

  const filteredUsers = useMemo(() => {
    if (!search) {
      return users;
    }

    return users.filter((u) =>
      JSON.stringify(u).toLowerCase().includes(search.toLowerCase())
    );
  }, [search, users]);

  const dataGrid = useMemo(
    () => (
      <DataGrid
        className={classes.tableWidget}
        columns={USERS_TABLE_COLUMNS}
        disableColumnMenu
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          columns: {
            columnVisibilityModel: {
              internal: !!currentUser?.internal,
              organization_count: selectedOrganizationId === 0,
              roles: selectedOrganizationId !== 0,
              action: userCanEdit
            }
          }
        }}
        pageSizeOptions={PAGE_SIZES}
        rows={filteredUsers}
        rowSelection={false}
        localeText={{ noRowsLabel: 'No data' }}
      />
    ),
    [filteredUsers]
  );

  return (
    <Box sx={{ height: '100%' }} className={classes.usersPage}>
      <Paper elevation={2} className={classes.usersPagePaper}>
        <Box
          className={classes.searchBox}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.5rem 1rem'
          }}
        >
          <QuickSearch onChange={(term) => setSearch(term)} border />

          <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
            <Tooltip title="User Roles and Permissions Overview">
              <FontAwesomeIcon
                icon={faInfoCircle}
                size="xl"
                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                onClick={() => setRolesDialogOpen(true)}
              />
            </Tooltip>
          </CheckPermissions>
        </Box>

        {isFetchingUsers && <LoadingIndicator isLoading />}
        {!isFetchingUsers && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>{dataGrid}</Box>
        )}
      </Paper>
      {open && (
        <UserEditDialog
          open={open}
          onClose={handleCloseUserDialog}
          user={user!}
        />
      )}
      {deleteOpen && (
        <UserDeleteDialog
          open={deleteOpen}
          onClose={handleCloseUserDialog}
          userId={user!.id}
        />
      )}
      {apiKeysDialogOpen && user && (
        <UserAuthKeysDialog
          open={apiKeysDialogOpen}
          onClose={() => setApiKeysDialogOpen(false)}
          userId={user.id}
          userName={user.name}
        />
      )}
      <CheckPermissions permissions={PERMISSIONS.USERS_UPDATE}>
        <ContextMenu
          menuList={USER_CONTEXT_MENU_LIST}
          ref={childRef}
          clickHandler={handleContextMenuClick}
        />
        <ResetMfaModal />
      </CheckPermissions>

      {rolesDialogOpen && <RolesPermissionDialog />}
    </Box>
  );
};

export default Users;
